import React, { useCallback, useEffect, useMemo, useState } from "react";
import TableContainer from "components/TableContainer";
import IntegrationsAPI from "restAPI/IntegrationsAPI";
import { CellRenderers } from "utils/tableUtils";
import { Button, Tooltip } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { PermissionSetMap } from "constants/RBACConstants";
import { RefreshIcon } from "components/Icons";
import message from "components/CustomMessage";
import AuthorisedContainer from "components/AuthorisedContainer";
import ArmorcodeAgentConfigForm from "./ArmorcodeAgentConfigForm";
import ModalUtil from "utils/modalUtil";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import dayjs from "dayjs";
import ActionMenuV2 from "components/ActionMenuV2";
import useTriggerableEffect from 'react-triggerable-effect';
import { AGENT_TYPES } from "./constants";

function ArmorCodeAgents() {
  const [data, setData] = useState();
  const [statusData, setStatusData] = useState();
  const featureFlags = useFeatureFlag();

  const fetch = useTriggerableEffect(() => {
    const { promise, cancel } = IntegrationsAPI.getArmorCodeAgentsV2();
    promise.then(setData).catch(e => { console.log(e); setData([]) });
    return cancel;
  }, []);

  useEffect(() => {
    const { promise, cancel } = IntegrationsAPI.getArmorCodeAgentsWebStatus();
    promise.then(response=>{setStatusData(response.data);}).catch(e => { console.log(e); setStatusData([])});
    return cancel;
  }, []);

  const deleteAgent = useCallback(async (id) => {
    try {
        await IntegrationsAPI.deleteArmorcodeAgent(id).promise;
        fetch();
    } catch (e) {
        console.log(e);
    }
  }, [fetch]);

  const onClick = useCallback(async (_, record) => {
    switch(record.name){
      case "delete":
        deleteAgent(record.data.id);
        break;
      default:
        break;
    }
  }, [deleteAgent]);

  const columns = useMemo(
    () => [
      {
        title: "Service Name",
        dataIndex: "name",
        render: CellRenderers.VALUE_OR_NA,
      },
      {
        title: "Service URL",
        dataIndex: "connectUrl",
        render: CellRenderers.VALUE_OR_NA,
        width: 200,
      },
      {
        title: "ArmorCode Server",
        dataIndex: "proxyDomain",
        render: (value, record) => record.type === AGENT_TYPES.TELEPORT ? 'N/A' : CellRenderers.VALUE_OR_NA(value),
      },
      {
        title: "Environment",
        dataIndex: "envName",
        render: CellRenderers.VALUE_OR_NA,
      },
      {
        title: "Agent Type",
        dataIndex: "type",
        render: CellRenderers.VALUE_OR_NA,
      },
      {
        title: "Tunnel Port",
        dataIndex: "tunnelPort",
        render: (value, record) => record.type === AGENT_TYPES.TELEPORT ? 'N/A' : CellRenderers.VALUE_OR_NA(value),
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (status, record) => <Tooltip title={record.error ? record.error : ""} placement="topLeft"><span>{CellRenderers.OPERATIONAL_STATUS(status)}</span></Tooltip>,
      },
      {
        title: "Action",
        dataIndex: "action",
        default: true,
        width: 50,
        alwaysDisplay: true,
        render: (active, record) => {
            const items = [{
                type: "delete",
                disabled: record.type !== AGENT_TYPES.TELEPORT,
                permission: PermissionSetMap.DELETE_TOOL,
            }]
            return (
                <div style={{marginTop:'4px'}}>
                    <ActionMenuV2 items={items} onClick={onClick} data={record} />
                </div>
            )
        }
      }
    ],
    [onClick]
  );

  const renderLastConnected = (_, record) => {
    if (!record.lastConnectedAt) return "N/A";
    const lastConnectedDate = dayjs(record.lastConnectedAt);
    return dayjs().diff(lastConnectedDate, "hour") < 1
        ? lastConnectedDate.fromNow()
        : lastConnectedDate.format("YYYY-MM-DD HH:mm:ss");
  };

  const statsColumns = useMemo(
    ()=>[
      {
        title: "Agent ID",
        dataIndex: "agentId",
        render: CellRenderers.VALUE_OR_NA,
      },
      {
        title: "Environment",
        dataIndex: "envName",
        render: CellRenderers.VALUE_OR_NA,
      },
      {
        title: "Last Connected",
        dataIndex: "lastConnectedAt",
        render: renderLastConnected,
      },
      {
        title: "Status",
        dataIndex: "status",
        render: CellRenderers.VALUE_OR_NA,
      }
    ],
    []
  )

  const syncToolStatus = useCallback(async() => {
    try {
     const response = await ToolsIntegrationsAPI.syncArmorCodeAgentStatus().promise;
      message.success(response);
    } catch (e){
      console.log(e);
    }
  }, []);

  const refreshData = useCallback(async() => {
    try {
      const { promise: agentsPromise } = IntegrationsAPI.getArmorCodeAgentsV2();
      const { promise: statusPromise } = IntegrationsAPI.getArmorCodeAgentsWebStatus();
      
      const [agentsData, statusResponse] = await Promise.all([
        agentsPromise,
        statusPromise
      ]);
      
      setData(agentsData);
      setStatusData(statusResponse.data);
      message.success("Data refreshed successfully");
    } catch (e) {
      console.log(e);
      message.error("Failed to refresh data");
    }
  }, []);

  const createWebAgentConfig = useCallback(async(data) => {
    const { promise, cancel } = IntegrationsAPI.createArmorCodeWebAgentConfig(data);
    promise.then((response) => {
      message.success("Config created successfully");
      //trigger sync after creation of config
      syncToolStatus()
    })
    .catch((e)=> {
      message.error(e.response.data.message);
    })
    return cancel;
  },[syncToolStatus])

  const showConfigForm = useCallback((e) => {
    ModalUtil.show({
      content: <ArmorcodeAgentConfigForm onFinish={createWebAgentConfig} />
    })
  }, [createWebAgentConfig]);

  return (
    <>
      <div className="text-right m-b-sm">
        {!featureFlags.WEB_AGENT_DISABLED ? <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
          <Button size="small" className="m-l-auto m-y-sm" type="primary" onClick={showConfigForm}>
            Add Configuration
          </Button>
        </AuthorisedContainer>
          : null}
        <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
          <Button type='default' size='small' className="m-l-sm m-y-sm" onClick={() => syncToolStatus()}>
            Sync Agent Status
          </Button>
        </AuthorisedContainer>
        <Button type='default' size='small' className="m-l-sm m-y-sm" onClick={() => refreshData()}>
          <RefreshIcon />
        </Button>
      </div>
      <TableContainer columns={columns} dataSource={data} loading={!data} />
      {!featureFlags.WEB_AGENT_DISABLED && 
        <TableContainer columns={statsColumns} title={"Web Agent Status"} dataSource={statusData} loading={!statusData} />
      }
    </>
  );
}

export default ArmorCodeAgents;
